import { Page, PageTitle } from "@/components/page"
import { CRUDLoader } from "@/components/page/crudloader"
import { CRUDToolbar } from "@/components/page/crudtoolbar"
import { useDatabaseData, useDatabaseItem, usePage } from "@/components/page/hooks"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { zodResolver } from "@hookform/resolvers/zod"
import { Database, Plus, Trash2 } from "lucide-react"
import { useForm, useFieldArray } from "react-hook-form"
import { z } from "zod"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Checkbox } from "@/components/ui/checkbox"
import { MultiSelect } from "@/components/ui/multi-select"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Combobox } from "@/components/ui/combobox"
import { countriesList, idToDomain } from "@/lib/agents/search/countries"

type KBSource = {
  id?: string
  title?: string
  sources?: Source[]
  team?: string
}

export const kbsources_crud = {
  title: "KB Source",
  route: "/docu/kbsources/:id",
  component,
  group: "docs",
  resource: "collections",
  icon: Database,
  action: "read",
} satisfies PageConfig

// interface Source {
//   type: "supabase" | "exa" | "weaviate" | "jina" | "databricks"
//   sources?: number[]
//   chunked?: boolean
//   locale?: string
//   domain?: string
//   distance?: number
//   results?: number
//   url?: string
// }

const SourceSchema = z.object({
  type: z.enum(["supabase", "supabased", "exa", "weaviate", "jina", "databricks", "youtube", "zendesk", "rescuenet", "services", "box"]),
  sources: z.array(z.number()).optional(),
  chunked: z.boolean().optional(),
  locale: z.string().optional(),
  domain: z.string().optional(),
  baseUrl: z.string().optional(),
  distance: z.number().min(0).max(2).optional(),
  results: z.number().int().min(1).max(100).optional(),
  url: z.string().url().optional(),
}).superRefine((data, ctx) => {
  if (data.type === "supabase" && (!data.sources || data.sources.length === 0)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "At least one vector source is required for Supabase", path: ["sources"] })
  }
  if (data.type === "supabased" && (!data.sources || data.sources.length === 0)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "At least one domain is required for Supabase By Domain", path: ["sources"] })
  }
  if (data.type === "exa" && data.domain) {
    if (!/^([a-z0-9]+(-[a-z0-9]+)*\.)+[a-z]{2,}$/i.test(data.domain)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Invalid domain format (e.g., google.com)", path: ["domain"] })
    }
  }
  // if (data.type === "exa") {
  //   if (!data.domain) {
  //     ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Domain is required for Exa", path: ["domain"] })
  //   } else if (!/^([a-z0-9]+(-[a-z0-9]+)*\.)+[a-z]{2,}$/i.test(data.domain)) {
  //     ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Invalid domain format (e.g., google.com)", path: ["domain"] })
  //   }
  // }
  if (data.type === "jina" && !data.url) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "URL is required for Jina", path: ["url"] })
  }
  if (data.type === "databricks" && !data.domain) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Index is required for Databricks", path: ["domain"] })
  }
  if (data.type === "youtube" && !data.domain) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Channel is required for YouTube", path: ["domain"] })
  }
  if (data.type === "zendesk" && !data.domain) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Domain is required for Zendesk", path: ["domain"] })
  }
  if (data.type === "services" && !data.domain) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Country is required for Services", path: ["domain"] })
  }
  if (data.type === "box" && !data.domain) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Folder URL is required for Box.net", path: ["domain"] })
  }
})


const FormSchema = z.object({
  title: z.string().min(1, "Title is required"),
  sources: z.array(SourceSchema).optional(),
})

type FormData = z.infer<typeof FormSchema>

export function component() {
  const { team } = usePage()
  const { data, id, loading, submit, toastSuccess, navigate } = useDatabaseItem("kbsources")
  const { data: vectors, loading: vectorsLoading } = useDatabaseData("vectors", {
    fields: ["id", "name"],
    filter: (supa) => supa.eq("isChunk", false).eq("team", team.id),
  })
  const { data: domains, loading: domainsLoading } = useDatabaseData("domains", {
    fields: ["id", "name"],
    filter: (supa) => supa.eq("team", team.id),
  })

  const form = useForm<FormData>({
    resolver: zodResolver(FormSchema),
    defaultValues: { title: data?.title || "", sources: (data?.sources as any) || [] },
    values: { title: data?.title || "", sources: (data?.sources as any) || [] },
  })

  const { fields, append, remove: removeSource } = useFieldArray({ control: form.control, name: "sources" })

  async function onSubmit(data: KBSource) {
    console.log("Submit: ", data)
    data.team = team.id
    await submit(data as any)
    toastSuccess()
    navigate("/docu/kbsources")
  }

  function addSource() {
    append({ type: "supabase", results: 10, distance: 1 })
  }

  if (loading || vectorsLoading || domainsLoading) return <CRUDLoader />

  const vectorOptions = vectors?.map((v: any) => ({ label: v.name, value: String(v.id) })) || []

  return <Page>
    <div className="pb-8">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <PageTitle>
            <CRUDToolbar table="kbsources" onSuccess={"/docu/kbsources"} />
          </PageTitle>

          <div className="rgrid">
            <div className="row">
              <div className="rc-12">
                <FormField control={form.control} name="title" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title</FormLabel>
                    <FormControl><Input {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
            </div>

            <div className="row mt-2">
              <div className="rc-12">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">Sources</h3>
                  <Button type="button" onClick={addSource} size="sm" variant="outline">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Source
                  </Button>
                </div>

                {fields.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    No sources added yet. Click "Add Source" to get started.
                  </div>
                )}

                <div className="space-y-4">
                  {fields.map((field, index) => {
                    const sourceType = form.watch(`sources.${index}.type`)
                    return (
                      <Card key={field.id} className="shadow-lg">
                        <CardHeader>
                          <div className="flex items-center justify-between -mt-6 -mb-8">
                            <CardTitle className="text-base">Source {index + 1}</CardTitle>
                            <Button type="button" variant="ghost" size="icon" onClick={() => removeSource(index)}>
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <div className="rgrid">
                            <div className="row">
                              <div className="rc-3">
                                <FormField control={form.control} name={`sources.${index}.type`} render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Type</FormLabel>
                                    <Select onValueChange={field.onChange} value={field.value}>
                                      <FormControl>
                                        <SelectTrigger>
                                          <SelectValue placeholder="Select type" />
                                        </SelectTrigger>
                                      </FormControl>
                                      <SelectContent>
                                        <SelectItem value="supabase">Supabase</SelectItem>
                                        <SelectItem value="supabased">Supabase By Domain</SelectItem>
                                        <SelectItem value="exa">Exa</SelectItem>
                                        <SelectItem value="weaviate">Weaviate</SelectItem>
                                        <SelectItem value="jina">Jina</SelectItem>
                                        <SelectItem value="databricks">Databricks</SelectItem>
                                        <SelectItem value="youtube">YouTube</SelectItem>
                                        <SelectItem value="zendesk">Zendesk</SelectItem>
                                        <SelectItem value="rescuenet">Rescue.net</SelectItem>
                                        <SelectItem value="services">Services</SelectItem>
                                        <SelectItem value="box">Box.net</SelectItem>
                                      </SelectContent>

                                    </Select>
                                    <FormMessage />
                                  </FormItem>
                                )} />
                              </div>

                              <div className="rc-3">
                                <FormField control={form.control} name={`sources.${index}.results`} render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Max Results</FormLabel>
                                    <FormControl>
                                      <Input type="number" {...field} onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : undefined)} value={field.value || 10} />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )} />
                              </div>

                              {(sourceType === "supabase" || sourceType === "supabased" || sourceType === "weaviate") && (
                                <>
                                  <div className="rc-3">
                                    <FormField control={form.control} name={`sources.${index}.locale`} render={({ field }) => (
                                      <FormItem>
                                        <FormLabel>Locale</FormLabel>
                                        <FormControl><Input {...field} placeholder="e.g., en-US" /></FormControl>
                                        <FormMessage />
                                      </FormItem>
                                    )} />
                                  </div>

                                  <div className="rc-3">
                                    <FormField control={form.control} name={`sources.${index}.distance`} render={({ field }) => (
                                      <FormItem>
                                        <FormLabel>Min Distance</FormLabel>
                                        <FormControl>
                                          <Input type="number" step="0.01" {...field} onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : undefined)} value={field.value || 1} />
                                        </FormControl>
                                        <FormMessage />
                                      </FormItem>
                                    )} />
                                  </div>
                                </>
                              )}
                            </div>

                            {sourceType === "supabase" && (
                              <div className="row">
                                <div className="rc-12">
                                  <FormField control={form.control} name={`sources.${index}.sources`} render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>Vector Sources</FormLabel>
                                      <FormControl>
                                        <MultiSelect options={vectorOptions} onValueChange={(values) => field.onChange(values.map(v => parseInt(v)))} defaultValue={field.value?.map(String) || []} placeholder="Select vectors" className="w-full" />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )} />
                                </div>
                              </div>
                            )}

                            {(sourceType === "supabase" || sourceType === "supabased") && (
                              <div className="row">
                                <div className="rc-12">
                                  <FormField control={form.control} name={`sources.${index}.chunked`} render={({ field }) => (
                                    <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                                      <FormControl>
                                        <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                                      </FormControl>
                                      <div className="space-y-1 leading-none">
                                        <FormLabel>Use Chunked Vectors</FormLabel>
                                      </div>
                                    </FormItem>
                                  )} />
                                </div>
                              </div>
                            )}

                            {sourceType === "supabased" && (
                              <div className="row">
                                <div className="rc-12">
                                  <FormField control={form.control} name={`sources.${index}.sources`} render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>Domains</FormLabel>
                                      <FormControl>
                                        <MultiSelect options={domains?.map((d: any) => ({ label: d.name, value: String(d.id) })) || []} onValueChange={(values) => field.onChange(values.map(v => parseInt(v)))} defaultValue={field.value?.map(String) || []} placeholder="Select domains" className="w-full" />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )} />
                                </div>
                              </div>
                            )}

                            {sourceType === "weaviate" && (
                              <div className="row">
                                <div className="rc-12">
                                  <FormField control={form.control} name={`sources.${index}.domain`} render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>Domain</FormLabel>
                                      <FormControl><Input {...field} placeholder="Any string" /></FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )} />
                                </div>
                              </div>
                            )}

                            {sourceType === "exa" && (
                              <div className="row">
                                <div className="rc-12">
                                  <FormField control={form.control} name={`sources.${index}.domain`} render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>Domain</FormLabel>
                                      <FormControl><Input {...field} placeholder="e.g., google.com" /></FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )} />
                                </div>
                              </div>
                            )}

                            {sourceType === "jina" && (
                              <div className="row">
                                <div className="rc-12">
                                  <FormField control={form.control} name={`sources.${index}.url`} render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>URL</FormLabel>
                                      <FormControl><Input {...field} placeholder="https://example.com" /></FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )} />
                                </div>
                              </div>
                            )}

                            {sourceType === "databricks" && (
                              <div className="row">
                                <div className="rc-12">
                                  <FormField control={form.control} name={`sources.${index}.domain`} render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>Index</FormLabel>
                                      <Select onValueChange={field.onChange} value={field.value}>
                                        <FormControl>
                                          <SelectTrigger>
                                            <SelectValue placeholder="Select index" />
                                          </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                          <SelectItem value="signpost_docs_vector">signpost_docs_vector</SelectItem>
                                          <SelectItem value="signpost_docs_llama_vector">signpost_docs_llama_vector</SelectItem>
                                          <SelectItem value="signpost">Signpost</SelectItem>
                                        </SelectContent>
                                      </Select>
                                      <FormMessage />
                                    </FormItem>
                                  )} />
                                </div>
                              </div>
                            )}

                            {sourceType === "youtube" && (
                              <div className="row">
                                <div className="rc-12">
                                  <FormField control={form.control} name={`sources.${index}.domain`} render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>Channel</FormLabel>
                                      <FormControl><Input {...field} placeholder="e.g., ahlansimsim" /></FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )} />
                                </div>
                              </div>
                            )}

                            {sourceType === "zendesk" && (
                              <div className="row">
                                <div className="rc-6">
                                  <FormField control={form.control} name={`sources.${index}.domain`} render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>Domain</FormLabel>
                                      <FormControl><Input {...field} placeholder="e.g., mycompany" /></FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )} />
                                </div>
                                <div className="rc-6">
                                  <FormField control={form.control} name={`sources.${index}.baseUrl`} render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>Base URL</FormLabel>
                                      <FormControl><Input {...field} placeholder="e.g., https://mycompany.zendesk.com" /></FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )} />
                                </div>
                              </div>
                            )}

                            {sourceType === "services" && (
                              <div className="row">
                                <div className="rc-12">
                                  <FormField control={form.control} name={`sources.${index}.domain`} render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>Country</FormLabel>
                                      <FormControl>
                                        <Combobox items={countriesList.map(c => ({ value: idToDomain[c.value], label: c.label }))} value={field.value} onChange={field.onChange} placeholder="Search country..." />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )} />
                                </div>
                              </div>
                            )}

                            {sourceType === "box" && (
                              <div className="row">
                                <div className="rc-12">
                                  <FormField control={form.control} name={`sources.${index}.domain`} render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>Folder URL</FormLabel>
                                      <FormControl><Input {...field} placeholder="https://app.box.com/folder/123456789" /></FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )} />
                                </div>
                              </div>
                            )}

                          </div>
                        </CardContent>
                      </Card>
                    )
                  })}
                </div>
              </div>
            </div>
          </div>
        </form>
      </Form>
    </div>
  </Page>
}
